import {
  Box,
  Flex,
  Button,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  useToast,
  Text as ChakraText,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import Card from "../../components/Card/Card";
import React, { useState, useEffect, useMemo, useRef } from "react";

import API_CONFIG from "../../Configuration/apiConfig.js";
import apiClient from "Configuration/apiClient";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { agGridStyles } from "../../components/Styles/AgGridStyles.js";
import { FaEdit, FaCheck, FaTimes, FaTrash } from "react-icons/fa";
import Select from "react-select";
import Draggable from "react-draggable";

// Extend agGridStyles to include custom style for editing row border
const customAgGridStyles = `
  ${agGridStyles}
  .ag-theme-alpine .ag-row.ag-row-editing {
    border: 1px solid #E2E8F0 !important;
  }
  .ag-theme-alpine .ag-row.ag-row-editing .ag-cell {
    border: 1px solid #E2E8F0 !important;
  }
  .ag-theme-alpine .ag-row.ag-row-editing .ag-cell-not-inline-editing {
    border: 1px solid #E2E8F0 !important;
  }
  .ag-theme-alpine .ag-row.ag-row-editing .ag-cell-inline-editing {
    border: 1px solid #E2E8F0 !important;
  }
  .ag-theme-alpine .ag-cell {
    text-align: left !important;
  }
  .ag-theme-alpine .ag-row {
    text-align: left !important;
  }
  /* Ensure modal is not clipped when dragged */
  .draggable-modal {
    position: absolute;
    z-index: 1000;
  }
`;

// Custom ClearIndicator for react-select
const CustomClearIndicator = (props) => {
  const {
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div {...restInnerProps} ref={ref} style={{ padding: "0 8px" }}>
      <FaTimes style={{ color: "#A0AEC0", cursor: "pointer", fontSize: "16px" }} />
    </div>
  );
};

export default function SourceDataUpdate() {
  const textColor = useColorModeValue("gray.700", "white");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageOptions, setImageOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [formData, setFormData] = useState({
    actualFileName: "",
    classId: "",
    productName: null,
    sourceQty: "",
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure();
  const [deleteParams, setDeleteParams] = useState(null);
  const cancelRef = useRef();
  const gridRef = useRef();
  const toast = useToast();

  // Fetch image options and product names on component mount
  useEffect(() => {
    const fetchImageOptions = async () => {
      try {
        const response = await apiClient.get(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOURCE_DETAILS.FILE_NAMES}`
        );
        if (Array.isArray(response.data)) {
          const options = response.data.map((option) => ({
            value: option,
            label: option,
          }));
          setImageOptions(options);
        } else {
          toast({
            title: "Error",
            description: "Invalid image data received.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      } catch (err) {
        toast({
          title: "Error",
          description: "Failed to fetch image options.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        console.error(err);
      }
    };

    const fetchProductOptions = async () => {
      try {
        const response = await apiClient.get(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOURCE_DETAILS.PRODUCT_NAMES}`
        );
        if (Array.isArray(response.data)) {
          const options = response.data.map((product) => ({
            value: product,
            label: product,
          }));
          setProductOptions(options);
        } else {
          toast({
            title: "Error",
            description: "Invalid product data received.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      } catch (err) {
        toast({
          title: "Error",
          description: "Failed to fetch product names.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        console.error(err);
      }
    };

    fetchImageOptions();
    fetchProductOptions();
  }, [toast]);

  // Update actualFileName in formData when selectedImage changes
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      actualFileName: selectedImage?.value || "",
    }));
  }, [selectedImage]);

  // Fetch classId when productName changes in the form
  useEffect(() => {
    const fetchClassId = async () => {
      if (formData.productName?.value) {
        try {
          const response = await apiClient.get(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOURCE_DETAILS.GET_CLASS_ID}?productName=${encodeURIComponent(formData.productName.value)}`
          );
          setFormData((prev) => ({
            ...prev,
            classId: response.data.toString(),
          }));
        } catch (err) {
          toast({
            title: "Error",
            description: "Failed to fetch class ID.",
            status: "error",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
          console.error(err);
        }
      } else {
        setFormData((prev) => ({
          ...prev,
          classId: "",
        }));
      }
    };
    fetchClassId();
  }, [formData.productName, toast]);

  // Handle delete row action
  const handleDelete = async (params) => {
    if (!selectedImage?.value) {
      toast({
        title: "Error",
        description: "Please select an image.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setLoading(true);
    try {
      const rowData = params.data;
      const payload = {
        imageFileName: rowData.imageFileName || selectedImage.value,
        actualFileName: rowData.actualFileName,
        classId: parseInt(rowData.classId, 10) || 0,
        productName: rowData.productName || "",
        sourceQty: parseInt(rowData.sourceQty, 10) || 0,
      };

      // Log the payload for debugging
      console.log("DELETE request payload:", payload);

      const response = await apiClient.delete(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOURCE_DETAILS.DELETE_RECORD}`,
        {
          data: payload,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Log the full response for debugging
      console.log("DELETE response:", response);

      // Check for success response (adjust based on actual API behavior)
      const successMessages = ["Data deleted successfully", "Record deleted"];
      if (
        successMessages.includes(response.data) ||
        successMessages.includes(response.data?.message) ||
        response.status === 200 ||
        response.status === 204
      ) {
        // Remove the deleted row from rowData
        setRowData((prevRowData) =>
          prevRowData.filter((row) => row.rowIndex !== params.node.rowIndex)
        );
        toast({
          title: "Success",
          description: "Record deleted successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        // Refresh the table data
        await handleDisplay();
      } else {
        toast({
          title: "Error",
          description: `Failed to delete record: ${response.data?.message || response.data || "Unknown error"}`,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (err) {
      // Log detailed error for debugging
      console.error("Delete error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast({
        title: "Error",
        description: `Failed to delete record: ${err.response?.data?.message || err.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (deleteParams) {
      await handleDelete(deleteParams);
    }
    onAlertClose();
  };

  // Open delete confirmation dialog
  const openDeleteConfirmation = (params) => {
    setDeleteParams(params);
    onAlertOpen();
  };

  // Table column definitions with Action column
  const columnDefs = useMemo(
    () => [
      {
        headerName: "File Name",
        field: "actualFileName",
        sortable: true,
        filter: true,
        floatingFilter: false,
        editable: true,
        cellEditor: "agTextCellEditor",
        cellStyle: { textAlign: "left" },
      },
      {
        headerName: "SKU Class ID",
        field: "classId",
        cellClass: "ag-numeric-cell",
        sortable: true,
        filter: true,
        floatingFilter: false,
        editable: true,
        cellEditor: "agNumberCellEditor",
      },
      {
        headerName: "SKU Name",
        field: "productName",
        sortable: true,
        filter: true,
        floatingFilter: false,
        editable: true,
        cellEditor: "agTextCellEditor",
      },
      {
        headerName: "Quantity",
        field: "sourceQty",
        cellClass: "ag-numeric-cell",
        sortable: true,
        filter: "agNumberColumnFilter",
        floatingFilter: false,
        editable: true,
        cellEditor: "agNumberCellEditor",
      },
      {
        headerName: "Action",
        field: "action",
        cellClass: "ag-numeric-cell",
        sortable: false,
        filter: false,
        width: 200,
        cellRenderer: (params) => {
          const isEditing = editingRowIndex === params.node.rowIndex;
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gap: "10px",
              }}
            >
              {isEditing ? (
                <>
                  <FaCheck
                    style={{ color: "#276749", cursor: "pointer", fontSize: "20px" }}
                    onClick={() => handleSaveEdit(params)}
                  />
                  <FaTimes
                    style={{ color: "#B91C1C", cursor: "pointer", fontSize: "20px" }}
                    onClick={() => {
                      params.api.stopEditing(true);
                      setEditingRowIndex(null);
                    }}
                  />
                </>
              ) : (
                <>
                  <FaEdit
                    style={{ color: "#005EB8", cursor: "pointer", fontSize: "20px" }}
                    onClick={() => {
                      params.api.startEditingCell({
                        rowIndex: params.node.rowIndex,
                        colKey: "actualFileName",
                      });
                      params.api.setFocusedCell(params.node.rowIndex, "actualFileName");
                      setEditingRowIndex(params.node.rowIndex);
                    }}
                  />
                  <FaTrash
                    style={{ color: "#B91C1C", cursor: "pointer", fontSize: "20px" }}
                    onClick={() => openDeleteConfirmation(params)}
                    title="Delete Row"
                  />
                </>
              )}
            </div>
          );
        },
      },
    ],
    [editingRowIndex]
  );

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      minWidth: 150,
      resizable: true,
      menuTabs: ["filterMenuTab"],
      cellStyle: { textAlign: "left" },
    }),
    []
  );

  // Fetch table data for the selected image (Display button action)
  const handleDisplay = async () => {
    if (!selectedImage?.value) {
      toast({
        title: "Error",
        description: "Please select an image.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOURCE_DETAILS.BY_IMAGE.replace(':imageName', selectedImage.value)}`
      );
      const apiData = Array.isArray(response.data) ? response.data : [response.data];
      const indexedData = apiData.map((row, index) => ({ ...row, rowIndex: index }));
      setRowData(indexedData);
      setEditingRowIndex(null);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to fetch data for the selected image.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Save the edited row data to the backend (Tick icon action)
  const handleSaveEdit = async (params) => {
    if (!selectedImage?.value) {
      toast({
        title: "Error",
        description: "Please select an image.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      params.api.stopEditing(true);
      setEditingRowIndex(null);
      return;
    }

    try {
      const rowData = { ...params.data };
      if (!rowData.actualFileName) {
        toast({
          title: "Error",
          description: "File Name is required.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        params.api.stopEditing(true);
        setEditingRowIndex(null);
        return;
      }

      const payload = {
        imageFileName: rowData.imageFileName || selectedImage.value,
        actualFileName: rowData.actualFileName,
        classId: parseInt(rowData.classId, 10) || 0,
        productName: rowData.productName || "",
        sourceQty: parseInt(rowData.sourceQty, 10) || 0,
        rowIndex: rowData.rowIndex,
      };

      setRowData((prevRowData) => {
        const updatedRowData = [...prevRowData];
        updatedRowData[params.node.rowIndex] = { ...rowData };
        return updatedRowData;
      });

      const response = await apiClient.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOURCE_DETAILS.UPDATE_RECORD.replace(':actualFileName', encodeURIComponent(rowData.actualFileName)).replace(':classId', rowData.classId)}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data === "Data updated successfully") {
        params.api.stopEditing(false);
        setEditingRowIndex(null);
        params.api.refreshCells({ force: true });
      } else {
        setRowData((prevRowData) => {
          const updatedRowData = [...prevRowData];
          updatedRowData[params.node.rowIndex] = { ...params.node.data };
          return updatedRowData;
        });
        toast({
          title: "Error",
          description: `Failed to save row: hospitality: "error"`,
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        params.api.stopEditing(true);
        setEditingRowIndex(null);
      }
    } catch (err) {
      setRowData((prevRowData) => {
        const updatedRowData = [...prevRowData];
        updatedRowData[params.node.rowIndex] = { ...params.node.data };
        return updatedRowData;
      });
      toast({
        title: "Error",
        description: `Failed to save row: ${err.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      console.error(err);
      params.api.stopEditing(true);
      setEditingRowIndex(null);
    }
  };

  // Update all rows for the selected image via POST request (Update button action)
  const handleUpdate = async () => {
    if (!selectedImage?.value) {
      toast({
        title: "Error",
        description: "Please select an image.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    setLoading(true);
    try {
      const updatedRows = [];
      gridRef.current.api.forEachNode((node) => {
        updatedRows.push(node.data);
      });

      if (updatedRows.length === 0) {
        toast({
          title: "Error",
          description: "No rows to update.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setLoading(false);
        return;
      }

      const errors = [];
      for (const row of updatedRows) {
        if (!row.actualFileName) {
          errors.push(`Row missing File Name at rowIndex: ${row.rowIndex}`);
          continue;
        }

        const payload = {
          imageFileName: row.imageFileName || selectedImage.value,
          actualFileName: row.actualFileName,
          classId: parseInt(row.classId, 10) || 0,
          productName: row.productName || "",
          sourceQty: parseInt(row.sourceQty, 10) || 0,
          rowIndex: row.rowIndex,
        };

        try {
          const response = await apiClient.post(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOURCE_DETAILS.UPDATE_RECORD.replace(':actualFileName', encodeURIComponent(row.actualFileName)).replace(':classId', row.classId)}`,
            payload,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );

          if (response.data !== "Data updated successfully") {
            errors.push(`Failed to update row with File Name: ${row.actualFileName}, rowIndex: ${row.rowIndex}`);
          }
        } catch (err) {
          errors.push(`Error updating row with File Name: ${row.actualFileName}, rowIndex: ${row.rowIndex} - ${err.message}`);
        }
      }

      if (errors.length > 0) {
        toast({
          title: "Error",
          description: `Some rows failed to update: ${errors.join("; ")}`,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      } else {
        await handleDisplay();
      }
    } catch (err) {
      toast({
        title: "Error",
        description: `Failed to update data: ${err.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submission to add new record
  const handleAddNew = async () => {
    setLoading(true);

    try {
      const payload = {
        imageFileName: selectedImage?.value || "null",
        actualFileName: formData.actualFileName.trim(),
        classId: parseInt(formData.classId, 10),
        productName: formData.productName?.value || "",
        sourceQty: parseInt(formData.sourceQty, 10),
      };

      // Validation
      if (!payload.actualFileName) {
        toast({
          title: "Error",
          description: "File Name is required.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setLoading(false);
        return;
      }
      if (!payload.productName) {
        toast({
          title: "Error",
          description: "SKU Name is required.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setLoading(false);
        return;
      }
      if (isNaN(payload.classId) || payload.classId < 0) {
        toast({
          title: "Error",
          description: "Valid SKU Class ID is required.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setLoading(false);
        return;
      }
      if (isNaN(payload.sourceQty) || payload.sourceQty < 0) {
        toast({
          title: "Error",
          description: "Valid Quantity is required.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setLoading(false);
        return;
      }

      const response = await apiClient.post(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SOURCE_DETAILS.ADD_RECORD}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Check for success response (adjusted for potential API response)
      const successMessages = ["Data added successfully", "Source data inserted successfully"];
      if (successMessages.includes(response.data) || response.status === 200) {
        toast({
          title: "Success",
          description: "New record added successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        setFormData({
          actualFileName: selectedImage?.value || "",
          classId: "",
          productName: null,
          sourceQty: "",
        });
        onClose();
        if (selectedImage?.value) {
          await handleDisplay();
        }
      } else {
        console.error("Unexpected API response:", response.data);
        toast({
          title: "Error",
          description: `Failed to add new record: ${response.data || "Unknown error"}`,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    } catch (err) {
      console.error("Error adding new record:", err.response?.data || err.message);
      toast({
        title: "Error",
        description: `Failed to add new record: ${err.response?.data?.message || err.message}`,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset form on modal close
  const handleModalClose = () => {
    setFormData({
      actualFileName: selectedImage?.value || "",
      classId: "",
      productName: null,
      sourceQty: "",
    });
    onClose();
  };

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <style>{customAgGridStyles}</style>
      <Card mb="20px" p="20px">
        <Flex justify="space-between" align="center" mb="20px">
          <Flex alignItems="center" gap="4">
            <Box width="200px">
              <Select
                options={imageOptions}
                value={selectedImage}
                onChange={(option) => {
                  setSelectedImage(option);
                  setRowData([]);
                  setFormData((prev) => ({ ...prev, actualFileName: option?.value || "" }));
                }}
                placeholder="Select Image"
                isSearchable
                isClearable
                components={{ ClearIndicator: CustomClearIndicator }}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderColor: "#E2E8F0",
                    fontSize: "14px",
                    padding: "2px",
                  }),
                  menu: (provided) => ({
                    ...provided,
                    zIndex: 9999,
                  }),
                  clearIndicator: (provided) => ({
                    ...provided,
                    padding: "0",
                  }),
                }}
              />
            </Box>
            <Button
              sx={{
                bg: "#C41E3A !important",
                color: "white !important",
                _hover: { bg: "#2A4365 !important" },
                _active: { bg: "#2A4365 !important" },
                _disabled: {
                  bg: "#2A4365 !important",
                  opacity: 0.4,
                  color: "white !important",
                },
              }}
              size="md"
              isDisabled={loading || !selectedImage}
              onClick={handleDisplay}
            >
              Display
            </Button>
          </Flex>
          <Flex gap="4">
            <Button
              sx={{
                bg: "#276749 !important",
                color: "white !important",
                _hover: { bg: "#22543D !important" },
                _active: { bg: "#22543D !important" },
                _disabled: {
                  bg: "#276749 !important",
                  opacity: 0.4,
                  color: "white !important",
                },
              }}
              size="md"
              isDisabled={loading || !selectedImage || rowData.length === 0}
              onClick={handleUpdate}
            >
              Update
            </Button>
            <Button
              sx={{
                bg: "#D69E2E !important",
                color: "white !important",
                _hover: { bg: "#B7791F !important" },
                _active: { bg: "#B7791F !important" },
                _disabled: {
                  bg: "#B7791F !important",
                  opacity: 0.4,
                  color: "white !important",
                },
              }}
              size="md"
              isDisabled={loading}
              onClick={onOpen}
            >
              Add New
            </Button>
          </Flex>
        </Flex>

        <Box
          className="custom-ag-grid ag-theme-alpine"
          height="650px"
          width="100%"
          border="2px solid"
          borderColor="gray.200"
          borderRadius="lg"
          overflow="hidden"
        >
          <AgGridReact
            ref={gridRef}
            rowData={rowData}
            rowHeight={50}
            headerHeight={60}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={15}
            suppressRowClickSelection={true}
            editType="fullRow"
            onCellValueChanged={(params) => {
              setRowData((prevRowData) => {
                const updatedRowData = [...prevRowData];
                updatedRowData[params.node.rowIndex] = { ...params.data };
                return updatedRowData;
              });
            }}
            loadingOverlayComponent={() => (
              <Flex justify="center" align="center" height="100%">
                <ChakraText color="gray.500">Loading...</ChakraText>
              </Flex>
            )}
            noRowsOverlayComponent={() => (
              <Flex justify="center" align="center" height="100%">
                <ChakraText color="gray.500">
                  {selectedImage ? "No data available" : "Please select an image"}
                </ChakraText>
              </Flex>
            )}
            onGridReady={(params) => {
              if (loading) params.api.showLoadingOverlay();
              else if (rowData.length === 0) params.api.showNoRowsOverlay();
              else params.api.hideOverlay();
            }}
          />
        </Box>

        <Modal isOpen={isOpen} onClose={handleModalClose}>
          <ModalOverlay />
          <Draggable handle=".modal-header">
            <ModalContent className="draggable-modal">
              <ModalHeader className="modal-header" cursor="move">
                Add New Record
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <FormControl mb="4" isRequired isDisabled={!selectedImage}>
                  <FormLabel>File Name</FormLabel>
                  <Input
                    name="actualFileName"
                    value={formData.actualFileName}
                    isDisabled={true}
                    placeholder="Select an image first"
                  />
                </FormControl>
                <FormControl mb="4" isRequired>
                  <FormLabel>SKU Name</FormLabel>
                  <Select
                    name="productName"
                    options={productOptions}
                    value={formData.productName}
                    onChange={(option) =>
                      setFormData((prev) => ({ ...prev, productName: option }))
                    }
                    placeholder="Select SKU Name"
                    isSearchable
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: "#E2E8F0",
                        fontSize: "14px",
                        padding: "2px",
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </FormControl>
                <FormControl mb="4" isRequired isDisabled>
                  <FormLabel>SKU Class ID</FormLabel>
                  <Input
                    name="classId"
                    type="number"
                    value={formData.classId}
                    placeholder="Auto-filled based on SKU Name"
                    isDisabled={true}
                  />
                </FormControl>
                <FormControl mb="4" isRequired>
                  <FormLabel>Quantity</FormLabel>
                  <Input
                    name="sourceQty"
                    type="number"
                    value={formData.sourceQty}
                    onChange={handleFormChange}
                    placeholder="e.g., 3"
                  />
                </FormControl>
              </ModalBody>
              <ModalFooter>
                <Button colorScheme="gray" mr={3} onClick={handleModalClose}>
                  Cancel
                </Button>
                <Button
                  sx={{
                    bg: "#276749 !important",
                    color: "white !important",
                    _hover: { bg: "#22543D !important" },
                    _active: { bg: "#22543D !important" },
                  }}
                  isDisabled={loading}
                  onClick={handleAddNew}
                >
                  Save
                </Button>
              </ModalFooter>
            </ModalContent>
          </Draggable>
        </Modal>

        <AlertDialog
          isOpen={isAlertOpen}
          leastDestructiveRef={cancelRef}
          onClose={onAlertClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Record
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to delete this record? This action cannot be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onAlertClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  onClick={handleConfirmDelete}
                  ml={3}
                  isDisabled={loading}
                >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Card>
    </Flex>
  );
}